import { getUserHasValidOrder, getUserIsBuy } from 'src/@api/login'
import { shouldEnterExpiredPurchasePage } from 'src/utils/subscriptionStatus'

/**
 * 进站入口判定。
 *
 * 后端 `user-device` 只返回「未过期，或过期不超过 30 天」的设备包，
 * 因此设备列表为空既可能是从没买过，也可能是所有包都过期超 30 天，
 * 前端无法再用「设备全部 lapsed」推断该不该进过期页。
 * 这里改为以后端状态为准，本地推算只作为接口不可用时的降级。
 *
 * 优先级固定：
 * 1. isBuy === false                    → 购买
 * 2. isBuy === true 且 valid === false  → 过期超 30 天页
 * 3. 其余                               → 设备 / 未绑单 / 绑定向导流程
 */
export const USER_ENTRY = Object.freeze({
  BUY: 'buy',
  EXPIRED: 'expired',
  CONTINUE: 'continue'
})

const readBoolean = value => (typeof value === 'boolean' ? value : null)

let flagsCache = null
let flagsLoading = null

/**
 * 读取 isBuy / shouldShowExpiredPage。
 * - isBuy：是否付过费（存在已支付订单，或存在设备记录）
 * - shouldShowExpiredPage：由 /tsafely-order/valid 的 valid 取反，表示最新一笔已支付订单已过期超 30 天
 *
 * 任一请求失败时对应字段为 null，调用方回退到本地推算，
 * 避免把接口异常当成「没买过」而把老用户踢去购买页。
 * @returns {Promise<{ isBuy: boolean|null, shouldShowExpiredPage: boolean|null }>}
 */
export const loadUserEntryFlags = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && flagsCache) return flagsCache
  if (flagsLoading) return flagsLoading

  flagsLoading = Promise.all([
    getUserIsBuy().catch(() => null),
    getUserHasValidOrder().catch(() => null)
  ])
    .then(([buyRes, validRes]) => {
      const hasValidOrder = readBoolean(validRes?.data?.valid)

      flagsCache = {
        isBuy: readBoolean(buyRes?.data?.isBuy),
        shouldShowExpiredPage: hasValidOrder === null ? null : !hasValidOrder
      }

      return flagsCache
    })
    .finally(() => {
      flagsLoading = null
    })

  return flagsLoading
}

export const readUserEntryFlags = () => flagsCache

export const clearUserEntryFlags = () => {
  flagsCache = null
}

/**
 * 按固定优先级给出入口结论；后端状态拿不到时才用 devices / 未绑单本地推算。
 * @param {Object} params
 * @param {{ isBuy: boolean|null, shouldShowExpiredPage: boolean|null }} [params.flags]
 * @param {Array} [params.devices]
 * @param {Array} [params.validUnboundOrders]
 * @returns {'buy'|'expired'|'continue'}
 */
export const resolveUserEntry = ({ flags, devices = [], validUnboundOrders = [] } = {}) => {
  const isBuy = flags?.isBuy ?? null
  const shouldShowExpiredPage = flags?.shouldShowExpiredPage ?? null

  if (isBuy === false) return USER_ENTRY.BUY
  if (isBuy === true && shouldShowExpiredPage === true) return USER_ENTRY.EXPIRED

  // 降级：接口未返回或请求失败时，沿用本地「设备全部 lapsed 且无未绑单」的推算
  if (isBuy === null || shouldShowExpiredPage === null) {
    if (shouldEnterExpiredPurchasePage({ devices, validUnboundOrders })) return USER_ENTRY.EXPIRED
  }

  return USER_ENTRY.CONTINUE
}
